'use client';

import { Slot } from '@radix-ui/react-slot';
import { AnimatePresence } from 'framer-motion';
import type { ButtonHTMLAttributes, MouseEvent, Ref } from 'react';

import { Icon, type Icons } from '../icon';
import { cn } from '../lib/utils';

import { useButtonGroup } from './group-context';
import { LoadingBar } from './loading-bar';
import {
  type Size,
  type Variant,
  contentLayer,
  disabledStyles,
  iconSize,
  interactiveBase,
  loadingStyles,
  variantStyles,
} from './tokens';
import { useAsyncClick } from './use-async-click';

const sizeStyles: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-body-13 rounded-lg',
  md: 'h-8 px-3 text-body-14 rounded-xl',
  lg: 'h-10 px-4 text-body-16 rounded-2xl',
  xl: 'h-14 px-6 text-body-18-bold rounded-3xl',
};

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  /** Visual style. Inherited from a parent `ButtonGroup` if present.
   *  @default 'primary' */
  variant?: Variant;
  /** Height/padding/typography token. Inherited from a parent `ButtonGroup`
   *  if present.
   *  @default 'md' */
  size?: Size;
  /** When `true`, the button stretches to fill its container's width. */
  fullWidth?: boolean;
  /** Optional icon rendered alongside the label. */
  iconName?: Icons;
  /** Side the icon sits on.
   *  @default 'left' */
  iconPosition?: 'left' | 'right';
  /** Controls the loading state. **Uncontrolled** when omitted: the button
   *  auto-detects a Promise returned from `onClick` and shows the loading
   *  bar until it resolves. **Controlled** when provided: your value wins
   *  and the Promise auto-detection is ignored — pass `isLoading={false}`
   *  to suppress the bar for incidental Promises (router navigate,
   *  fire-and-forget telemetry), or `isLoading={mutation.isPending}` to
   *  drive it from react-query. Subsequent clicks are still blocked
   *  while the Promise is pending regardless of this prop. */
  isLoading?: boolean;
  /** Sync or async click handler. If it returns a Promise and `isLoading`
   *  is not provided, the button shows a loading bar until the Promise
   *  resolves. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => unknown;
  /** When `true`, renders the rendered child directly via Radix `Slot` instead
   *  of a `<button>` — useful for wrapping a `<Link>` while keeping the styles. */
  asChild?: boolean;
  /** Forwarded to the underlying `<button>` (or the `asChild` element). */
  ref?: Ref<HTMLButtonElement>;
};

export const Button = ({
  variant: variantProp,
  size: sizeProp,
  fullWidth = false,
  iconName,
  iconPosition = 'left',
  isLoading,
  onClick,
  asChild = false,
  className,
  type = 'button',
  disabled = false,
  children,
  ref,
  ...props
}: Props) => {
  const group = useButtonGroup();
  const variant = group?.variant ?? variantProp ?? 'primary';
  const size = group?.size ?? sizeProp ?? 'md';

  // HTML `disabled` is reserved for the permanently-unavailable state from the
  // parent. During loading we use `aria-disabled` + `aria-busy` so the button
  // stays focusable and screen readers announce the state change.
  const { handleClick, isPending } = useAsyncClick({
    onClick,
    // `=== true` is load-bearing: `isLoading` is tri-state and we only want
    // to block clicks when explicitly controlled to `true`. Simplifying to
    // `disabled || isLoading` would break the `isLoading={false}` opt-out.
    isBlocked: disabled || isLoading === true,
  });
  // Controlled when `isLoading` is provided (the explicit value wins);
  // uncontrolled when omitted (the Promise return from `onClick` drives
  // the bar). Mirrors the React controlled/uncontrolled pattern.
  const showSpinner = isLoading ?? isPending;

  const Comp = asChild ? Slot : 'button';
  // `font-medium` overrides the inherited 600 from `text-body-*-bold` (xl) so
  // the icon-font (single-weight) doesn't get a smudgy synthetic bold.
  const icon = iconName ? (
    <Icon name={iconName} className={cn('shrink-0 font-medium', iconSize[size])} />
  ) : null;

  const content = (
    <>
      <AnimatePresence>{showSpinner && <LoadingBar variant={variant} />}</AnimatePresence>
      <span data-slot="content" className={contentLayer}>
        {iconPosition === 'left' && icon}
        {children}
        {iconPosition === 'right' && icon}
      </span>
    </>
  );

  return (
    <Comp
      ref={ref}
      {...(asChild ? {} : { type })}
      disabled={disabled}
      aria-disabled={showSpinner ? true : undefined}
      aria-busy={showSpinner ? true : undefined}
      onClick={handleClick}
      className={cn(
        interactiveBase,
        'whitespace-nowrap font-medium',
        sizeStyles[size],
        variantStyles[variant],
        showSpinner ? loadingStyles : disabledStyles,
        fullWidth ? 'w-full' : 'w-auto',
        className,
      )}
      {...props}
    >
      {/* Slot needs exactly one child — wrap in `display: contents` when asChild. */}
      {asChild ? <span className="contents">{content}</span> : content}
    </Comp>
  );
};

export type { Props as ButtonProps };
